// src/components/ConversationHistory.tsx
// Historial de conversaciones con patrón expandible tipo acordeón

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getConversations, getThreads as apiGetThreads, getThreadResources, getContactName, type ThreadResourceDTO } from "../lib/api";
import type { ConversationLog, ThreadDTO } from "../types";
import { 
  Loader, RefreshCw, MessageSquare, Phone, User, 
  ChevronDown, ChevronUp, Bot, Download, FileText, Image, 
  File, Clock, Search, X
} from "lucide-react";

// =====================================================
// Tipos 
// =====================================================
export type ConversationThread = {
  id: string; org_id: string; session_id: string; telefono: string;
  customer_id?: string | null; owner_user_id?: string | null;
  status: "open" | "waiting_admin" | "waiting_user" | "scheduled" | "closed" | string;
  priority: "urgent" | "atender" | "neutral" | string;
  last_message_at: string; last_message_preview?: string | null;
  last_sentiment?: "positivo" | "neutro" | "negativo" | string | null;
  unread_admin_count: number; meta?: any;
  contactName?: string | null;
};

type Cat = "all" | "urgente" | "revision" | "neutra";
type Estado = "all" | "open_like" | "open" | "waiting_admin" | "waiting_user" | "scheduled" | "closed";

// =====================================================
// Funciones de Utilidad
// =====================================================
const URGENT_KWS = ["urgente", "urgent", "prioridad", "inmediato", "auxilio", "soporte", "alta", "high"];
const REVIEW_KWS = ["revisión", "revision", "review", "pendiente", "validar", "confirmar", "verificar"];

const normalizePriority = (p?: string | null): "urgent" | "atender" | "neutral" => {
  const s = String(p ?? "").toLowerCase().trim();
  if (["urgent", "urgente", "alta", "high", "prio_alta"].includes(s)) return "urgent";
  if (["atender", "revision", "revisión", "review", "pendiente"].includes(s)) return "atender";
  return "neutral";
};

const derivePriority = (t: ThreadDTO): "urgent" | "atender" | "neutral" => {
  const base = normalizePriority(t.priority);
  if (base !== "neutral") return base;
  const m = (t.meta || {}) as Record<string, any>;
  const metaP = normalizePriority(m.priority ?? m.prioridad ?? m.prio ?? m.categoria ?? m.category);
  if (metaP !== "neutral") return metaP;
  const text = (t.last_message_preview || "").toLowerCase();
  if (URGENT_KWS.some((k) => text.includes(k))) return "urgent";
  if (REVIEW_KWS.some((k) => text.includes(k))) return "atender";
  return "neutral";
};

const toCat = (p: ConversationThread["priority"] | string): Exclude<Cat, "all"> => {
  const np = normalizePriority(p as string);
  return np === "urgent" ? "urgente" : np === "atender" ? "revision" : "neutra";
};

function extractAssistantPreview(raw?: string | null): string {
  const s = (raw ?? "").trim();
  if (!s || !s.startsWith('{')) return s.slice(0, 200);
  try {
    const obj = JSON.parse(s);
    if (typeof obj !== 'object' || obj === null) return s.slice(0, 200);
    const previewText = obj.respuesta || obj.resumen || obj.summary;
    if (typeof previewText === 'string' && previewText.trim()) return previewText.trim();
    const bloques = Array.isArray(obj.bloques) ? obj.bloques : [];
    const firstText = bloques.find(b => b?.tipo === "texto" && typeof b.contenido === 'string');
    if (firstText?.contenido) return String(firstText.contenido).trim();
    return "[Respuesta interactiva]";
  } catch { return s.slice(0, 200); }
}

const normalizeThread = (t: ThreadDTO): ConversationThread => ({
  id: t.id,
  org_id: t.org_id ?? "",
  session_id: t.session_id,
  telefono: t.telefono,
  customer_id: t.customer_id ?? null,
  owner_user_id: t.owner_user_id ?? null,
  status: (t.status as ConversationThread["status"]) ?? "open",
  priority: derivePriority(t),
  last_message_at: t.last_message_at,
  last_message_preview: extractAssistantPreview(t.last_message_preview),
  last_sentiment: (t.last_sentiment as ConversationThread["last_sentiment"]) ?? null,
  unread_admin_count: t.unread_admin_count ?? 0,
  meta: t.meta ?? {},
});

const normalizeStatus = (s?: string | null): ConversationThread["status"] => {
  const v = String(s ?? "").toLowerCase().trim();
  if (["open", "waiting_admin", "waiting_user", "scheduled", "closed"].includes(v)) return v as any;
  return "open";
};

const isOpenLike = (s?: string | null) => {
  const v = normalizeStatus(s);
  return v === "open" || v === "waiting_admin" || v === "waiting_user" || v === "scheduled";
};

const formatDateTime = (d: string) => new Date(d).toLocaleString('es-MX', { 
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
});

const formatTime = (d: string) => new Date(d).toLocaleTimeString('es-MX', { 
  hour: '2-digit', minute: '2-digit' 
});

// Colores de estado/prioridad
const priorityStyles: Record<string, { border: string; text: string; bg: string }> = {
  urgente: { border: 'border-red-500/40', text: 'text-red-400', bg: 'bg-red-500/20' },
  revision: { border: 'border-amber-500/40', text: 'text-amber-400', bg: 'bg-amber-500/20' },
  neutra: { border: 'border-sky-500/40', text: 'text-sky-400', bg: 'bg-sky-500/20' },
};

const statusLabels: Record<string, string> = {
  open: 'Abierto',
  waiting_admin: 'Esperando admin',
  waiting_user: 'Esperando usuario',
  scheduled: 'Agendado',
  closed: 'Cerrado',
};

// Icono de archivo según tipo
function FileIcon({ type, format }: { type: string; format: string }) {
  if (type === 'image' || format?.startsWith('image/')) return <Image size={14} className="text-purple-400" />;
  if (format?.includes('pdf')) return <FileText size={14} className="text-red-400" />;
  if (format?.includes('word') || format?.includes('doc')) return <FileText size={14} className="text-blue-400" />;
  return <File size={14} className="text-white/60" />;
}

// =====================================================
// Hooks de Datos
// =====================================================
const useThreads = () => {
  const [threads, setThreads] = useState<ConversationThread[]>([]);
  const [status, setStatus] = useState<"loading" | "error" | "success">("loading");
  
  const fetchThreads = useCallback(async () => {
    setStatus("loading");
    try {
      const dtos = await apiGetThreads({ limit: 100, offset: 0 });
      const normalized = dtos.map(normalizeThread);
      
      // Intentar obtener nombres de contacto para cada thread
      const withNames = await Promise.all(normalized.map(async (t) => {
        try {
          const name = await getContactName(t.session_id);
          return { ...t, contactName: name };
        } catch {
          return t;
        }
      }));
      
      setThreads(withNames);
      setStatus("success");
    } catch (e) {
      console.error("Error al cargar threads", e);
      setStatus("error");
    }
  }, []);
  
  useEffect(() => { fetchThreads(); }, [fetchThreads]);
  return { threads, status, refresh: fetchThreads };
};

// =====================================================
// Componente de Fila de Thread (Acordeón)
// =====================================================
function ThreadRow({ 
  thread, 
  isExpanded, 
  onToggle 
}: { 
  thread: ConversationThread; 
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const [messages, setMessages] = useState<ConversationLog[]>([]);
  const [resources, setResources] = useState<ThreadResourceDTO[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  
  const cat = toCat(thread.priority);
  const styles = priorityStyles[cat];
  
  // Cargar datos al expandir
  useEffect(() => {
    if (isExpanded && messages.length === 0) {
      setLoadingData(true);
      Promise.all([
        getConversations(undefined, thread.session_id),
        getThreadResources(thread.id).catch(() => []),
      ]).then(([msgs, res]) => {
        setMessages(msgs || []);
        setResources(res || []);
      }).finally(() => setLoadingData(false));
    }
  }, [isExpanded, thread.id, thread.session_id, messages.length]);

  // Descargar recurso
  const handleDownload = (resource: ThreadResourceDTO) => {
    const filename = resource.path.split('/').pop() || 'archivo';
    const baseUrl = import.meta.env.VITE_API_BASE || '';
    window.open(`${baseUrl}/resources/${resource.id}/download`, '_blank');
  };

  return (
    <div className={`bg-white/5 border ${isExpanded ? 'border-white/20' : 'border-white/10'} rounded-xl overflow-hidden hover:border-white/20 transition-colors`}>
      {/* Header clickeable */}
      <button 
        type="button" 
        onClick={onToggle} 
        className="w-full text-left p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Nombre y teléfono */}
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-white/60" />
                <h3 className="text-white font-semibold">
                  {thread.contactName || thread.telefono}
                </h3>
              </div>
              {thread.contactName && (
                <span className="text-white/50 text-sm">({thread.telefono})</span>
              )}
              {/* Badge de prioridad */}
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles.bg} ${styles.text}`}>
                {cat}
              </span>
              {/* Badge de estado */}
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/10 text-white/70">
                {statusLabels[normalizeStatus(thread.status)] || thread.status}
              </span>
              {/* Unread count */}
              {!!thread.unread_admin_count && (
                <span className="h-5 w-5 grid place-items-center rounded-full bg-sky-500 text-white text-xs font-bold">
                  {thread.unread_admin_count}
                </span>
              )}
            </div>
            
            {/* Preview del mensaje */}
            <p className="text-white/70 text-sm line-clamp-2 mt-1">
              {thread.last_message_preview || "Sin mensajes"}
            </p>
            
            {/* Fecha */}
            <div className="flex items-center gap-1 mt-2 text-white/50 text-xs">
              <Clock size={12} />
              {formatDateTime(thread.last_message_at)}
            </div>
          </div>
          
          {/* Chevron */}
          <div className="flex-shrink-0 mt-1">
            {isExpanded ? (
              <ChevronUp size={18} className="text-white/40" />
            ) : (
              <ChevronDown size={18} className="text-white/40" />
            )}
          </div>
        </div>
      </button>

      {/* Contenido expandido */}
      {isExpanded && (
        <div className="border-t border-white/10 bg-black/20">
          {loadingData ? (
            <div className="flex justify-center items-center py-8">
              <Loader className="animate-spin text-white/40" />
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Recursos/Archivos */}
              {resources.length > 0 && (
                <div>
                  <h4 className="text-white/80 font-medium text-sm mb-2 flex items-center gap-2">
                    <FileText size={14} className="text-purple-400" />
                    Archivos ({resources.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {resources.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => handleDownload(r)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white/80 hover:bg-white/10 hover:border-white/20 transition-colors"
                      >
                        <FileIcon type={r.type} format={r.format} />
                        <span className="truncate max-w-[150px]">
                          {r.path.split('/').pop() || 'Archivo'}
                        </span>
                        <Download size={14} className="text-white/40" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Mensajes */}
              <div>
                <h4 className="text-white/80 font-medium text-sm mb-3 flex items-center gap-2">
                  <MessageSquare size={14} className="text-sky-400" />
                  Conversación ({messages.length} mensajes)
                </h4>
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
                  {messages.map((m, idx) => (
                    <MessageBubble key={idx} msg={m} />
                  ))}
                  {messages.length === 0 && (
                    <p className="text-white/40 text-sm text-center py-4">
                      No hay mensajes en esta conversación
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =====================================================
// Burbuja de Mensaje
// =====================================================

// Extrae texto legible de un mensaje (puede ser JSON o texto plano)
function extractMessageText(raw: string | null | undefined): string {
  if (!raw) return '';
  const text = raw.trim();
  
  // Si no es JSON, devolver como está
  if (!text.startsWith('{')) return text;
  
  try {
    const data = JSON.parse(text);
    
    // Formato: { respuesta: "..." }
    if (data.respuesta) return data.respuesta;
    
    // Formato: { bloques: [{ tipo: "texto", contenido: "..." }] }
    if (Array.isArray(data.bloques)) {
      const textos = data.bloques
        .filter((b: any) => b.tipo === 'texto' && b.contenido)
        .map((b: any) => b.contenido);
      if (textos.length > 0) return textos.join('\n\n');
    }
    
    // Formato: { message: "..." }
    if (data.message) return data.message;
    
    // No pudimos extraer, devolver JSON formateado parcialmente
    return text;
  } catch {
    return text;
  }
}

function MessageBubble({ msg }: { msg: ConversationLog }) {
  const isUser = msg.role === "user";
  
  // Skip inicio messages
  if (msg.role === 'inicio') return null;
  
  // Extraer texto legible del mensaje
  const displayText = extractMessageText(msg.message);
  
  return (
    <div className={`w-full flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[85%] rounded-2xl px-3 py-2",
          "border backdrop-blur-md shadow-lg transition",
          isUser
            ? "bg-sky-500/10 border-sky-400/25 shadow-[0_0_12px_rgba(14,165,233,.15)]"
            : "bg-purple-500/10 border-purple-400/25 shadow-[0_0_12px_rgba(168,85,247,.15)]",
        ].join(" ")}
      >
        <div className="flex items-center gap-2 text-[11px] opacity-70 mb-1">
          <div className={`p-1 rounded-md ${isUser ? "bg-sky-500/20" : "bg-purple-500/20"}`}>
            {isUser ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
          </div>
          <span>{isUser ? "Usuario" : "Asistente"}</span>
          <span className="opacity-60">•</span>
          <span>{formatTime(msg.timestamp)}</span>
        </div>
        <p className="whitespace-pre-wrap break-words leading-relaxed text-sm text-white/90">
          {displayText}
        </p>
      </div>
    </div>
  );
}

// =====================================================
// Filtros
// =====================================================
function FilterBar({
  search, setSearch,
  cat, setCat,
  estado, setEstado,
  counts,
  onRefresh,
  loading,
}: {
  search: string;
  setSearch: (v: string) => void;
  cat: Cat;
  setCat: (v: Cat) => void;
  estado: Estado;
  setEstado: (v: Estado) => void;
  counts: { all: number; u: number; r: number; n: number; byStatus: Record<string, number> };
  onRefresh: () => void;
  loading: boolean;
}) {
  return (
    <div className="space-y-3 mb-4">
      {/* Search + Refresh */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por teléfono o mensaje..."
            className="w-full pl-10 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/40 focus:border-sky-500/50 outline-none"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>
      
      {/* Chips de filtro */}
      <div className="flex flex-wrap gap-2">
        {/* Prioridad */}
        <button
          onClick={() => setCat('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            cat === 'all' 
              ? 'bg-white/10 border-white/30 text-white' 
              : 'bg-transparent border-white/10 text-white/50 hover:border-white/20'
          }`}
        >
          Todas ({counts.all})
        </button>
        <button
          onClick={() => setCat('urgente')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            cat === 'urgente' 
              ? 'bg-red-500/20 border-red-500/50 text-red-400' 
              : 'bg-transparent border-red-500/20 text-red-400/50 hover:border-red-500/40'
          }`}
        >
          Urgentes ({counts.u})
        </button>
        <button
          onClick={() => setCat('revision')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            cat === 'revision' 
              ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' 
              : 'bg-transparent border-amber-500/20 text-amber-400/50 hover:border-amber-500/40'
          }`}
        >
          Revisión ({counts.r})
        </button>
        
        {/* Estado */}
        <div className="w-px bg-white/10 mx-1" />
        <button
          onClick={() => setEstado(estado === 'open_like' ? 'all' : 'open_like')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            estado === 'open_like' 
              ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' 
              : 'bg-transparent border-white/10 text-white/50 hover:border-white/20'
          }`}
        >
          Abiertas ({counts.byStatus.open_like})
        </button>
        <button
          onClick={() => setEstado(estado === 'closed' ? 'all' : 'closed')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            estado === 'closed' 
              ? 'bg-gray-500/20 border-gray-500/50 text-gray-400' 
              : 'bg-transparent border-white/10 text-white/50 hover:border-white/20'
          }`}
        >
          Cerradas ({counts.byStatus.closed})
        </button>
      </div>
    </div>
  );
}

// =====================================================
// Componente Principal
// =====================================================
function ConversationHistoryComp() {
  const { threads, status, refresh } = useThreads();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState<Cat>("all");
  const [estado, setEstado] = useState<Estado>("all");

  const toggle = (id: string) => setExpandedId(prev => prev === id ? null : id);

  const filteredThreads = useMemo(() => {
    if (status !== 'success') return [];
    const q = search.toLowerCase().trim();
    let arr = threads;
    
    // Filtro de búsqueda
    if (q) {
      arr = arr.filter(t => 
        t.telefono.toLowerCase().includes(q) ||
        (t.contactName || '').toLowerCase().includes(q) ||
        (t.last_message_preview || '').toLowerCase().includes(q)
      );
    }
    
    // Filtro de prioridad
    if (cat !== "all") arr = arr.filter(t => toCat(t.priority) === cat);
    
    // Filtro de estado
    if (estado !== 'all') {
      if (estado === 'open_like') arr = arr.filter(t => isOpenLike(t.status));
      else arr = arr.filter(t => normalizeStatus(t.status) === estado);
    }
    
    return arr;
  }, [threads, search, cat, estado, status]);

  // Conteos
  const counts = useMemo(() => {
    const all = threads.length;
    const u = threads.filter(t => toCat(t.priority) === 'urgente').length;
    const r = threads.filter(t => toCat(t.priority) === 'revision').length;
    const n = all - u - r;
    const byStatus = {
      all,
      open_like: threads.filter(t => isOpenLike(t.status)).length,
      open: threads.filter(t => normalizeStatus(t.status) === 'open').length,
      waiting_admin: threads.filter(t => normalizeStatus(t.status) === 'waiting_admin').length,
      waiting_user: threads.filter(t => normalizeStatus(t.status) === 'waiting_user').length,
      scheduled: threads.filter(t => normalizeStatus(t.status) === 'scheduled').length,
      closed: threads.filter(t => normalizeStatus(t.status) === 'closed').length,
    };
    return { u, r, n, all, byStatus };
  }, [threads]);

  return (
    <div className="w-full h-full p-4 overflow-hidden flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between mb-4">
        <h1 className="font-semibold text-gradient text-xl">Historial de Conversaciones</h1>
        <span className="text-white/50 text-sm">{filteredThreads.length} conversaciones</span>
      </header>

      {/* Filtros */}
      <FilterBar
        search={search}
        setSearch={setSearch}
        cat={cat}
        setCat={setCat}
        estado={estado}
        setEstado={setEstado}
        counts={counts}
        onRefresh={refresh}
        loading={status === 'loading'}
      />

      {/* Lista */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1 scrollbar-hide">
        {status === "loading" && (
          <div className="flex justify-center items-center py-12">
            <Loader className="animate-spin text-white/40" size={24} />
          </div>
        )}
        
        {status === "error" && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <MessageSquare className="text-red-400 mb-2" size={32} />
            <p className="text-red-400">Error al cargar conversaciones</p>
            <button
              onClick={refresh}
              className="mt-3 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-colors text-sm"
            >
              Reintentar
            </button>
          </div>
        )}
        
        {status === "success" && filteredThreads.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <MessageSquare className="text-white/30 mb-2" size={32} />
            <p className="text-white/50">No hay conversaciones</p>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="mt-2 text-sky-400 text-sm hover:underline"
              >
                Limpiar búsqueda
              </button>
            )}
          </div>
        )}
        
        {status === "success" && filteredThreads.map(thread => (
          <ThreadRow
            key={thread.id}
            thread={thread}
            isExpanded={expandedId === thread.id}
            onToggle={() => toggle(thread.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default ConversationHistoryComp;
export const ConversationHistory = ConversationHistoryComp;
